import {useCallback, useEffect, useRef, useState} from 'react'

import {useSuiContext} from '@s-ui/react-context'

import {FARE_TYPES, FINANCING_STATUS} from '../config.js'
import {FINANCING_STRATEGIES} from '../strategies.js'
import {getEntryError} from '../utils.js'
import {useGetTrackingEvents} from './useGetTrackingEvents.js'

/**
 * An empty field means "nothing to quote", which is not the same as a 0 € entry — several
 * ads legitimately quote `minEntry: 0`. Only the empty case collapses to `null`.
 */
const parseEntry = value => (value === '' || value == null ? null : Number(value))

const getUpdatedFare = (normalized, fareType) =>
  normalized.fares.find(f => f.fareType === fareType) ?? normalized.fares[0]

export const useFinancingCalculations = ({ad, financingData, mode, vehicleInfo}) => {
  const {domain} = useSuiContext()
  const strategy = mode ? FINANCING_STRATEGIES[mode] : null

  const {TransactionNoContentError} = domain.get('config').get('COMMON').ERRORS

  const {
    trackUpdatedCalculationsEvents,
    trackEntryChangeEvents,
    trackTermsChangeEvents,
    trackCTA,
    trackLoanAplicationFailed,
    trackLegalContent
  } = useGetTrackingEvents()

  const [data, setData] = useState(null)
  const [status, setStatus] = useState(
    strategy?.calculationsUseCase ? FINANCING_STATUS.LOADING : FINANCING_STATUS.READY
  )
  const [entryValue, setEntryValue] = useState(String(financingData?.minEntry ?? ''))
  const [appliedEntry, setAppliedEntry] = useState(null)
  const [appliedTerms, setAppliedTerms] = useState(financingData?.terms ?? null)
  const [selectedTerms, setSelectedTerms] = useState(financingData?.terms ?? null)
  const [selectedFare, setSelectedFare] = useState(FARE_TYPES.WITHOUT_INSURANCE)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isCtaLoading, setIsCtaLoading] = useState(false)
  const [hasCtaError, setHasCtaError] = useState(false)
  const [entryError, setEntryError] = useState(null)
  const [hasCommittedEntry, setHasCommittedEntry] = useState(false)
  const [calculationsErrorKey, setCalculationsErrorKey] = useState(null)

  // A blur and a terms change can be in flight at once; only the newest response may win.
  const latestRequestRef = useRef(0)

  const fetchCalculations = useCallback(
    async ({entry, terms, withLifeInsurance} = {}) => {
      if (!strategy?.calculationsUseCase) return {isStale: true}

      const requestId = ++latestRequestRef.current
      const [error, response] = await domain
        .get(strategy.calculationsUseCase)
        .execute(strategy.buildCalculationsParams({ad, entry, financingData, terms, withLifeInsurance}))

      if (requestId !== latestRequestRef.current) return {isStale: true}
      if (error || !response) return {error}

      return {normalized: strategy.normalize({ad, financingData, response})}
    },
    [ad, domain, financingData, strategy]
  )

  useEffect(() => {
    if (!strategy) return

    if (!strategy.calculationsUseCase) {
      setData(strategy.normalize({ad, financingData}))
      setStatus(FINANCING_STATUS.READY)
      return
    }

    const init = async () => {
      const {normalized} = await fetchCalculations()
      if (!normalized) {
        setStatus(FINANCING_STATUS.ERROR)
        return
      }

      const [firstFare = {}] = normalized.fares
      const initialEntry = firstFare.minEntry ?? financingData?.minEntry ?? null
      const initialTerms = firstFare.terms ?? financingData?.terms ?? null
      setEntryValue(String(initialEntry ?? ''))
      setSelectedTerms(initialTerms)
      setAppliedEntry(initialEntry)
      setAppliedTerms(initialTerms)
      setData(normalized)
      setStatus(FINANCING_STATUS.READY)
    }

    init()
  }, [ad, fetchCalculations, financingData, strategy])

  /**
   * A rejected re-quote leaves nothing to show: the previous quote is still on screen, so the inputs
   * have to go back to the values that produced it or the panel reads as if the new numbers applied.
   */
  const refresh = useCallback(
    async params => {
      setIsRefreshing(true)
      setCalculationsErrorKey(null)
      const {error, isStale, normalized} = await fetchCalculations(params)

      // A newer request is already in flight and owns both the data and the spinner.
      if (isStale) return null

      if (normalized) {
        setAppliedEntry(parseEntry(params?.entry))
        setAppliedTerms(params?.terms ?? null)
        setData(normalized)
      } else {
        // A 204 means the lender understood the request and has no offer for those numbers, which the
        // user can fix by trying others. Anything else is ours, and reads as the service being down.
        setCalculationsErrorKey(
          error instanceof TransactionNoContentError
            ? 'PANEL_FINANCING.B2C.ERROR_NO_CONTENT_LOAN_CALCULATIONS'
            : 'COMMON.SERVICE_DOWN_TRY_LATER'
        )
        setEntryValue(appliedEntry != null ? String(appliedEntry) : '')
        setSelectedTerms(appliedTerms)
      }
      setIsRefreshing(false)
      return normalized ?? null
    },
    [TransactionNoContentError, appliedEntry, appliedTerms, fetchCalculations]
  )

  const hasLifeInsurance = selectedFare === FARE_TYPES.WITH_INSURANCE

  const fares = data?.fares ?? []
  const currentFare = fares.find(({fareType}) => fareType === selectedFare) ?? fares[0]

  const onEntryChange = value => {
    setEntryValue(value)
    setCalculationsErrorKey(null)
    setEntryError(getEntryError({maxEntry: currentFare?.maxEntry, minEntry: currentFare?.minEntry, value}))
  }

  // Settling the entry is driven by blur AND by Enter — typing alone never re-quotes.
  const onEntryCommit = async () => {
    setHasCommittedEntry(true)

    // An out-of-range entry is never quoted — it snaps back to the minimum the lender accepts.
    const entry = entryError ? String(currentFare?.minEntry ?? '') : entryValue
    if (entryError) {
      setEntryValue(entry)
      setEntryError(null)
    }

    if (parseEntry(entry) === appliedEntry) return

    trackEntryChangeEvents(ad, entry)
    const normalized = await refresh({entry, terms: selectedTerms, withLifeInsurance: hasLifeInsurance})
    if (normalized)
      trackUpdatedCalculationsEvents(ad, {...getUpdatedFare(normalized, selectedFare), entry: parseEntry(entry)})
  }

  const onTermsChange = async value => {
    const terms = Number(value)
    setSelectedTerms(terms)
    trackTermsChangeEvents(ad, value)
    const normalized = await refresh({entry: entryValue, terms, withLifeInsurance: hasLifeInsurance})
    if (normalized)
      trackUpdatedCalculationsEvents(ad, {...getUpdatedFare(normalized, selectedFare), entry: parseEntry(entryValue)})
  }

  const onFareChange = async fareType => {
    setSelectedFare(fareType)
    const normalized = await refresh({
      entry: entryValue,
      terms: selectedTerms,
      withLifeInsurance: fareType === FARE_TYPES.WITH_INSURANCE
    })
    if (normalized)
      trackUpdatedCalculationsEvents(ad, {...getUpdatedFare(normalized, fareType), entry: parseEntry(entryValue)})
  }

  const onCtaClick = async () => {
    setIsCtaLoading(true)
    setHasCtaError(false)
    try {
      const url = await strategy.resolveCtaUrl({
        ad,
        currentFare,
        data,
        domain,
        entryValue,
        selectedFare,
        selectedTerms,
        vehicleInfo
      })
      if (!url) {
        setHasCtaError(true)
        trackLoanAplicationFailed(ad)
        return
      }
      trackCTA(ad, {...currentFare, entry: parseEntry(entryValue)})
      if (typeof window !== 'undefined') window.open(url)
    } finally {
      setIsCtaLoading(false)
    }
  }

  return {
    appliedEntry,
    calculationsErrorKey,
    cashPrice: data?.cashPrice,
    currentFare,
    entryError,
    entryValue,
    fares,
    hasCommittedEntry,
    hasCtaError,
    isCtaLoading,
    isRefreshing,
    onCtaClick,
    onEntryCommit,
    onEntryChange,
    onFareChange,
    onTermsChange,
    selectedFare,
    selectedTerms,
    status,
    trackLegalContent
  }
}
